import { useEffect, useState } from "react";
import { Zap, Settings, Loader2, Wallet, Users, QrCode, Copy, X } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import { useApp } from "@/context/AppContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Keypair, Transaction, SystemProgram, Connection, PublicKey, VersionedTransaction } from "@solana/web3.js"; 
import bs58 from "bs58";
import { QRCodeSVG } from "qrcode.react";
import { getSolanaRpcUrl } from "@/lib/solanaRpc";
import { decodeSolanaSecretBytes } from "@/lib/solanaWalletSecret";
import { fetchNativeSolBalance, fetchSplTokenBalance } from "@/lib/solanaBalance";
import { postPumpPortalLocalTrade, buildPumpPortalLocalTradeTransaction } from "@/lib/pumpPortalLightningTrade";
import { sendJitoBundle } from "@/lib/jitoBundler";
import { tokenMetaCache } from "@/lib/tokenMetaCache";

interface ChartQuickTradeProps {
  mint: string;
  tradingMode?: "paper" | "real";
}

interface SubWallet {
  id: string;
  publicKey: string;
  secretKey: Uint8Array;
  solBalance: number;
  tokenBalance: number;
  allocSol?: number;
  slippageBps?: number;
}

export function ChartQuickTrade({ mint, tradingMode = "real" }: ChartQuickTradeProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Collapsed by default on mobile so it doesn't cover the chart
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const [activeTab, setActiveTab] = useState<"standard" | "advanced">("standard");

  // On desktop, auto-expand; stay collapsed on mobile until user taps
  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);
  
  const { connected, publicKey, secretKey, subWallets: configSubWallets, createSubWallets, updateSubWallet } = useWallet();
  const { heliusKey, addAppToast } = useApp() as any;
  
  // Standard Trade State
  const [tradeAmountBuy, setTradeAmountBuy] = useState<string>("0.1");
  const [tradeAmountSell, setTradeAmountSell] = useState<string>("100%");

  // Trade Mode States
  const [buyMode, setBuyMode] = useState<"market" | "limit">("market");
  const [sellMode, setSellMode] = useState<"market" | "limit">("market");
  const [advBuyMode, setAdvBuyMode] = useState<"market" | "limit">("market");
  const [advSellMode, setAdvSellMode] = useState<"market" | "limit">("market");

  // Limit Orders Config State
  const [buyLimitType, setBuyLimitType] = useState<"price" | "marketcap">("price");
  const [buyLimitValue, setBuyLimitValue] = useState("");
  const [sellLimitType, setSellLimitType] = useState<"price" | "marketcap">("price");
  const [sellLimitValue, setSellLimitValue] = useState("");
  const [advBuyLimitType, setAdvBuyLimitType] = useState<"price" | "marketcap">("price");
  const [advBuyLimitValue, setAdvBuyLimitValue] = useState("");
  const [advSellLimitType, setAdvSellLimitType] = useState<"price" | "marketcap">("price");
  const [advSellLimitValue, setAdvSellLimitValue] = useState("");

  // Balances
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);

  // Advanced Mode (Multi-Wallet) State
  const [subWallets, setSubWallets] = useState<SubWallet[]>([]);

  useEffect(() => {
    setSubWallets(prev => {
      const next: SubWallet[] = configSubWallets.map(c => {
        const existing = prev.find(p => p.id === c.id);
        if (existing) return existing;
        const secretBytes = decodeSolanaSecretBytes(c.secretKey) || new Uint8Array(64);
        return {
          id: c.id,
          publicKey: c.publicKey,
          secretKey: secretBytes,
          solBalance: 0,
          tokenBalance: 0
        };
      });
      const isSame = next.length === prev.length && next.every((n, i) => n.id === prev[i].id);
      return isSame ? prev : next;
    });
  }, [configSubWallets]);
  const [numWalletsToGen, setNumWalletsToGen] = useState<string>("3");
  const [bundleBuyAmount, setBundleBuyAmount] = useState<string>("0.05");
  const [fundingWallet, setFundingWallet] = useState<SubWallet | null>(null);
  const [mainFundAmount, setMainFundAmount] = useState<string>("0.05");

  useEffect(() => {
    if (!publicKey || !connected) return;
    fetchNativeSolBalance(publicKey).then(setSolBalance);
    if (mint) {
      fetchSplTokenBalance(publicKey, mint).then(setTokenBalance);
    }
    
    const interval = setInterval(() => {
      fetchNativeSolBalance(publicKey).then(setSolBalance);
      if (mint) fetchSplTokenBalance(publicKey, mint).then(setTokenBalance);
    }, 10000);
    return () => clearInterval(interval);
  }, [publicKey, connected, mint]);

  const handlePlaceLimitOrder = async (
    action: "buy" | "sell",
    triggerType: "price" | "marketcap",
    triggerValue: number,
    amount: string,
    walletAddr: string = publicKey?.toString() || ""
  ) => {
    if (!walletAddr) {
      if (addAppToast) addAppToast("Please connect wallet first", "error");
      return;
    }
    if (!triggerValue || isNaN(triggerValue) || triggerValue <= 0) {
       if (addAppToast) addAppToast("Please enter a valid trigger target", "error");
       return;
    }
    try {
      const meta = tokenMetaCache.get(mint || "");
      const res = await fetch("/api/limit-orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          walletAddress: walletAddr,
          mint: mint || "",
          symbol: meta?.symbol || "TOKEN",
          action,
          triggerType,
          triggerValue,
          amount
        })
      });
      if (res.ok) {
        if (addAppToast) addAppToast(`Limit ${action.toUpperCase()} placed successfully`, "success");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuickBuy = async (amount: number = Number(tradeAmountBuy)) => {
    if (!mint || isTrading || tradingMode !== "real" || isNaN(amount) || amount <= 0) return;
    setIsTrading(true);
    try {
      if (!connected || !secretKey || !publicKey || !heliusKey) {
        if (addAppToast) addAppToast("Wallet not connected", "error");
        return;
      }
      const result = await postPumpPortalLocalTrade(secretKey, heliusKey, {
        publicKey, mint, action: "buy", amount, denominatedInSol: "true", slippage: 10, priorityFee: 0.001,
      });
      if (addAppToast) {
         if (result.ok) addAppToast("Buy Transaction Sent!", "success");
         else addAppToast(result.message || "Buy Failed", "error");
      }
    } catch (error) {
       if (addAppToast) addAppToast(error instanceof Error ? error.message : "Unknown error", "error");
    } finally {
      setIsTrading(false);
    }
  };

  const handleQuickSell = async (pct: string = tradeAmountSell) => {
    if (!mint || isTrading || tradingMode !== "real") return;
    setIsTrading(true);
    try {
      if (!connected || !secretKey || !publicKey || !heliusKey) {
        if (addAppToast) addAppToast("Wallet not connected", "error");
        return;
      }
      const result = await postPumpPortalLocalTrade(secretKey, heliusKey, {
        publicKey, mint, action: "sell", amount: pct.includes("%") ? pct : `${pct}%`, denominatedInSol: "false", slippage: 10, priorityFee: 0.001,
      });
      if (addAppToast) {
         if (result.ok) addAppToast("Sell Transaction Sent!", "success");
         else addAppToast(result.message || "Sell Failed", "error");
      }
    } catch (error) {
      if (addAppToast) addAppToast(error instanceof Error ? error.message : "Unknown error", "error");
    } finally {
      setIsTrading(false);
    }
  };

  const generateSubWallets = () => {
    const count = parseInt(numWalletsToGen) || 3;
    createSubWallets(count);
  };

  const refreshSubWalletBalances = async () => {
    const updated = await Promise.all(subWallets.map(async (w) => {
      const sol = await fetchNativeSolBalance(w.publicKey);
      const token = mint ? await fetchSplTokenBalance(w.publicKey, mint) : 0;
      return {
        ...w,
        solBalance: sol ?? w.solBalance,
        tokenBalance: token ?? w.tokenBalance,
      };
    }));
    setSubWallets(updated);
  };

  const handleFundAllFromMain = async () => {
    if (!publicKey || !secretKey || !connected || subWallets.length === 0) return;
    const amount = Number(mainFundAmount);
    if (isNaN(amount) || amount <= 0) return;
    setIsTrading(true);
    try {
      const connection = new Connection(getSolanaRpcUrl());
      const mainKp = Keypair.fromSecretKey(bs58.decode(secretKey));
      const tx = new Transaction();
      for (const w of subWallets) {
        tx.add(SystemProgram.transfer({
          fromPubkey: mainKp.publicKey,
          toPubkey: new PublicKey(w.publicKey),
          lamports: Math.floor(amount * 1e9),
        }));
      }
      const { blockhash } = await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
      tx.feePayer = mainKp.publicKey;
      tx.sign(mainKp);
      await connection.sendRawTransaction(tx.serialize());
      setTimeout(refreshSubWalletBalances, 2500);
      if (addAppToast) addAppToast("Funding sent", "success");
    } catch (err) {
      console.error("Fund All Error:", err);
      if (addAppToast) addAppToast("Funding failed", "error");
    } finally {
      setIsTrading(false);
    }
  };

  const handleBundleBuy = async () => {
    if (!mint || !heliusKey || subWallets.length === 0) return;
    const globalAmount = Number(bundleBuyAmount);
    setIsTrading(true);
    try {
      const txs = await Promise.all(subWallets.map(async (w) => {
        const amount = w.allocSol ?? globalAmount;
        const slippage = w.slippageBps ? w.slippageBps / 100 : 15;
        if (w.solBalance > 0.005 && amount > 0) {
           const build = await buildPumpPortalLocalTradeTransaction(bs58.encode(w.secretKey), heliusKey, {
             publicKey: w.publicKey, mint, action: "buy", amount, denominatedInSol: "true", slippage, priorityFee: 0.001
           });
           if (build.ok) return build.transaction;
        }
        return null;
      }));
      const validTxs = txs.filter((tx): tx is VersionedTransaction => tx !== null);
      if (validTxs.length > 0) {
        const feePayerWallet = subWallets.find(w => w.solBalance > 0.002);
        if (feePayerWallet) {
          const feePayer = Keypair.fromSecretKey(feePayerWallet.secretKey);
          for (let i = 0; i < validTxs.length; i += 4) {
            const chunk = validTxs.slice(i, i + 4);
            await sendJitoBundle(chunk, feePayer);
          }
        }
        if (addAppToast) addAppToast("Bundle Buy Sent", "success");
      }
      setTimeout(refreshSubWalletBalances, 3000);
    } finally {
      setIsTrading(false);
    }
  };

  const handleBundleSell = async () => {
    if (!mint || !heliusKey || subWallets.length === 0) return;
    setIsTrading(true);
    try {
      const txs = await Promise.all(subWallets.map(async (w) => {
        if (w.tokenBalance > 0) {
           const slippage = w.slippageBps ? w.slippageBps / 100 : 15;
           const build = await buildPumpPortalLocalTradeTransaction(bs58.encode(w.secretKey), heliusKey, {
             publicKey: w.publicKey, mint, action: "sell", amount: "100%", denominatedInSol: "false", slippage, priorityFee: 0.001
           });
           if (build.ok) return build.transaction;
        }
        return null;
      }));
      const validTxs = txs.filter((tx): tx is VersionedTransaction => tx !== null);
      if (validTxs.length > 0) {
        const feePayerWallet = subWallets.find(w => w.solBalance > 0.002);
        if (feePayerWallet) {
          const feePayer = Keypair.fromSecretKey(feePayerWallet.secretKey);
          for (let i = 0; i < validTxs.length; i += 4) {
            const chunk = validTxs.slice(i, i + 4);
            await sendJitoBundle(chunk, feePayer);
          }
        }
        if (addAppToast) addAppToast("Bundle Sell Sent", "success");
      }
      setTimeout(refreshSubWalletBalances, 3000);
    } finally {
      setIsTrading(false);
    }
  };

  const handleConsolidate = async () => {
    if (!publicKey) return;
    setIsTrading(true);
    try {
      const connection = new Connection(getSolanaRpcUrl());
      const mainPub = new PublicKey(publicKey);
      const tx = new Transaction();
      const signers: Keypair[] = [];
      let feePayer: Keypair | null = null;
      
      for (const w of subWallets) {
        const bal = await fetchNativeSolBalance(w.publicKey);
        if (bal && bal > 0.003) {
           const subKp = Keypair.fromSecretKey(w.secretKey);
           if (!feePayer) feePayer = subKp;
           const transferAmount = Math.floor((bal - 0.002) * 1e9);
           tx.add(SystemProgram.transfer({
             fromPubkey: subKp.publicKey, toPubkey: mainPub, lamports: transferAmount
           }));
           signers.push(subKp);
        }
      }

      if (signers.length > 0 && feePayer) {
         const { blockhash } = await connection.getLatestBlockhash();
         tx.recentBlockhash = blockhash;
         tx.feePayer = feePayer.publicKey;
         tx.sign(...signers);
         await connection.sendRawTransaction(tx.serialize());
         if (addAppToast) addAppToast("Consolidation sent", "success");
      }
      setTimeout(refreshSubWalletBalances, 3000);
    } catch (err) {
      console.error("Consolidate Error:", err);
      if (addAppToast) addAppToast("Consolidation failed", "error");
    } finally {
      setIsTrading(false);
    }
  };

  const formatNumber = (n: number, decimals = 2) => {
    if (n == null || isNaN(n)) return "0";
    if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}M`;
    if (n >= 1_000) return `${(n / 1_000).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}K`;
    return `${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };
  const formatSol = (n: number) => {
    if (n == null || isNaN(n)) return "0.000 SOL";
    return `${n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} SOL`;
  };

  if (!isExpanded) {
    return (
      <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-xl p-2 flex items-center justify-between w-[240px] shadow-2xl pointer-events-auto">
         <div className="flex bg-black/40 rounded-lg p-0.5 border border-white/10">
            <button
              onClick={() => setActiveTab("standard")}
              className={`px-3 py-1 rounded text-[10px] font-bold uppercase transition-all ${activeTab === "standard" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"}`}
            >
              Std
            </button>
            <button
              onClick={() => setActiveTab("advanced")}
              className={`px-3 py-1 rounded text-[10px] font-bold flex items-center gap-1 uppercase transition-all ${activeTab === "advanced" ? "bg-blue-500/20 text-blue-400" : "text-white/40 hover:text-white/70"}`}
            >
              <Users className="size-3" /> Adv
            </button>
          </div>
          <button
            onClick={() => setIsExpanded(true)}
            className="p-1 rounded bg-white/5 hover:bg-white/10 transition-all text-white/60 hover:text-white"
          >
            <Settings className="size-3.5" />
          </button>
      </div>
    );
  }

  return (
    <div className={`bg-black/80 backdrop-blur-md border border-white/10 rounded-xl flex flex-col shadow-2xl pointer-events-auto max-h-[70vh] overflow-hidden transition-all ${activeTab === "standard" ? "w-[560px]" : "w-[680px]"}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 shrink-0">
          <div className="flex bg-black/40 rounded-lg p-0.5 border border-white/10">
            <button
              onClick={() => setActiveTab("standard")}
              className={`px-3 py-1 rounded text-[10px] font-bold uppercase transition-all ${activeTab === "standard" ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"}`}
            >
              {t("panel.standard")}
            </button>
            <button
              onClick={() => setActiveTab("advanced")}
              className={`px-3 py-1 rounded text-[10px] font-bold flex items-center gap-1 uppercase transition-all ${activeTab === "advanced" ? "bg-blue-500/20 text-blue-400" : "text-white/40 hover:text-white/70"}`}
            >
              <Users className="size-3" /> {t("panel.advanced")}
            </button>
          </div>
          <button
            onClick={() => setIsExpanded(false)}
            className="p-1 rounded bg-white/5 hover:bg-white/10 transition-all text-white/60 hover:text-white"
          >
            <X className="size-3.5" />
          </button>
      </div>

      <div className="p-3 overflow-y-auto custom-scrollbar flex flex-col gap-3">
            {activeTab === "standard" ? (
              <div className="flex gap-3">
                {/* BUY */}
                <div className="flex-1 bg-black/40 border border-white/10 rounded-xl p-3 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[12px] font-black uppercase text-emerald-400">{t("panel.buy")}</span>
                    <span className="text-[10px] font-mono text-white/50">Bal: {solBalance !== null ? formatSol(solBalance) : "--"}</span>
                  </div>
                  
                  <div className="flex bg-neutral-900 rounded-lg p-0.5 mb-3 border border-white/5">
                    <button onClick={() => setBuyMode("market")} className={`flex-1 text-[10px] py-1 rounded-md font-bold transition-all ${buyMode === "market" ? "bg-emerald-500/20 text-emerald-400" : "text-white/40 hover:text-white/80"}`}>Market</button>
                    <button onClick={() => setBuyMode("limit")} className={`flex-1 text-[10px] py-1 rounded-md font-bold transition-all ${buyMode === "limit" ? "bg-emerald-500/20 text-emerald-400" : "text-white/40 hover:text-white/80"}`}>Limit</button>
                  </div>

                  {buyMode === "market" ? (
                    <>
                      <div className="relative mb-3">
                        <input
                          type="text"
                          value={tradeAmountBuy}
                          onChange={(e) => setTradeAmountBuy(e.target.value)}
                          className={`w-full h-10 text-base bg-neutral-950 border rounded-xl pl-3 pr-10 font-mono text-white placeholder:text-white/20 outline-none border-emerald-500/20 focus:border-emerald-500/50`}
                          placeholder="0.00"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase text-white/30">SOL</span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-3">
                        {["0.1", "0.5", "1", "5"].map(amt => (
                          <button key={amt} onClick={() => setTradeAmountBuy(amt)} className="flex-1 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/5 text-[9px] font-mono text-white/70">
                            {amt}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => handleQuickBuy()}
                        disabled={isTrading || tradingMode !== "real"}
                        className="w-full h-10 text-[11px] rounded-xl flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all disabled:opacity-50 bg-emerald-500 text-black hover:bg-emerald-400 mt-auto"
                      >
                        {isTrading ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />} {t("panel.buy").toUpperCase()}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Trigger</label>
                          <select value={buyLimitType} onChange={(e) => setBuyLimitType(e.target.value as any)} className="w-full h-8 bg-neutral-950 border border-white/10 rounded-lg px-2 text-[11px] text-white focus:outline-none focus:border-emerald-500/50">
                            <option value="price">Price (SOL)</option>
                            <option value="marketcap">Marketcap</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Target</label>
                          <input type="number" step="any" placeholder="0.00" value={buyLimitValue} onChange={(e) => setBuyLimitValue(e.target.value)} className="w-full h-8 bg-neutral-950 border border-white/10 rounded-lg px-2 text-[11px] font-mono text-white placeholder:text-white/20 focus:outline-none focus:border-emerald-500/50" />
                        </div>
                      </div>
                      <div className="relative mb-3 mt-auto">
                        <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Amount</label>
                        <input
                          type="text"
                          value={tradeAmountBuy}
                          onChange={(e) => setTradeAmountBuy(e.target.value)}
                          className={`w-full h-8 text-[11px] bg-neutral-950 border rounded-lg pl-2 pr-8 font-mono text-white placeholder:text-white/20 outline-none border-white/10 focus:border-emerald-500/50`}
                          placeholder="0.00"
                        />
                        <span className="absolute right-2 bottom-1.5 text-[8px] font-black uppercase text-white/30">SOL</span>
                      </div>
                      <button
                        onClick={() => handlePlaceLimitOrder("buy", buyLimitType, parseFloat(buyLimitValue), tradeAmountBuy)}
                        disabled={tradingMode !== "real"}
                        className="w-full h-10 text-[11px] rounded-xl flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all disabled:opacity-50 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500 border border-emerald-500/30 hover:text-black mt-auto"
                      >
                        Place Limit Buy
                      </button>
                    </>
                  )}
                </div>

                {/* SELL */}
                <div className="flex-1 bg-black/40 border border-white/10 rounded-xl p-3 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[12px] font-black uppercase text-rose-400">{t("panel.sell")}</span>
                    <span className="text-[10px] font-mono text-white/50">Bal: {tokenBalance !== null ? formatNumber(tokenBalance) : "--"}</span>
                  </div>
                  
                  <div className="flex bg-neutral-900 rounded-lg p-0.5 mb-3 border border-white/5">
                    <button onClick={() => setSellMode("market")} className={`flex-1 text-[10px] py-1 rounded-md font-bold transition-all ${sellMode === "market" ? "bg-rose-500/20 text-rose-400" : "text-white/40 hover:text-white/80"}`}>Market</button>
                    <button onClick={() => setSellMode("limit")} className={`flex-1 text-[10px] py-1 rounded-md font-bold transition-all ${sellMode === "limit" ? "bg-rose-500/20 text-rose-400" : "text-white/40 hover:text-white/80"}`}>Limit</button>
                  </div>

                  {sellMode === "market" ? (
                    <>
                      <div className="relative mb-3">
                        <input
                          type="text"
                          value={tradeAmountSell}
                          onChange={(e) => setTradeAmountSell(e.target.value)}
                          className={`w-full h-10 text-base bg-neutral-950 border rounded-xl pl-3 pr-10 font-mono text-white placeholder:text-white/20 outline-none border-rose-500/20 focus:border-rose-500/50`}
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black uppercase text-white/30">%</span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-3">
                        {["25%", "50%", "75%", "100%"].map(pct => (
                          <button key={pct} onClick={() => setTradeAmountSell(pct)} className="flex-1 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/5 text-[9px] font-mono text-white/70">
                            {pct}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => handleQuickSell()}
                        disabled={isTrading || tradingMode !== "real"}
                        className="w-full h-10 text-[11px] rounded-xl flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all disabled:opacity-50 bg-rose-500 text-black hover:bg-rose-400 mt-auto"
                      >
                        {isTrading ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />} {t("panel.sell").toUpperCase()}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Trigger</label>
                          <select value={sellLimitType} onChange={(e) => setSellLimitType(e.target.value as any)} className="w-full h-8 bg-neutral-950 border border-white/10 rounded-lg px-2 text-[11px] text-white focus:outline-none focus:border-rose-500/50">
                            <option value="price">Price (SOL)</option>
                            <option value="marketcap">Marketcap</option>
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Target</label>
                          <input type="number" step="any" placeholder="0.00" value={sellLimitValue} onChange={(e) => setSellLimitValue(e.target.value)} className="w-full h-8 bg-neutral-950 border border-white/10 rounded-lg px-2 text-[11px] font-mono text-white placeholder:text-white/20 focus:outline-none focus:border-rose-500/50" />
                        </div>
                      </div>
                      <div className="relative mb-3 mt-auto">
                        <label className="block text-[9px] uppercase tracking-wider text-white/40 mb-1">Amount</label>
                        <input
                          type="text"
                          value={tradeAmountSell}
                          onChange={(e) => setTradeAmountSell(e.target.value)}
                          className={`w-full h-8 text-[11px] bg-neutral-950 border rounded-lg pl-2 pr-8 font-mono text-white placeholder:text-white/20 outline-none border-white/10 focus:border-rose-500/50`}
                          placeholder="100%"
                        />
                        <span className="absolute right-2 bottom-1.5 text-[8px] font-black uppercase text-white/30">%</span>
                      </div>
                      <button
                        onClick={() => handlePlaceLimitOrder("sell", sellLimitType, parseFloat(sellLimitValue), tradeAmountSell)}
                        disabled={tradingMode !== "real"}
                        className="w-full h-10 text-[11px] rounded-xl flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all disabled:opacity-50 bg-rose-500/20 text-rose-400 hover:bg-rose-500 border border-rose-500/30 hover:text-black mt-auto"
                      >
                        Place Limit Sell
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                {/* ADVANCED MULTI-WALLET: BUNDLER SETUP & BUY */}
                <div className="flex-[0.4] bg-black/40 border border-blue-500/20 rounded-xl p-3 flex flex-col">
                  <h3 className="text-[11px] font-black uppercase text-blue-400 mb-3 flex items-center gap-2">
                    <Settings className="size-3" /> Bundler Setup & Buy
                  </h3>
                  
                  {/* Setup & Fund */}
                  <div className="flex gap-2 mb-2">
                    <div className="relative flex-[0.7]">
                      <input 
                        type="number" 
                        value={numWalletsToGen}
                        onChange={(e) => setNumWalletsToGen(e.target.value)}
                        className="w-full bg-black/50 border border-white/10 rounded-lg py-1.5 pl-2 pr-8 text-xs font-mono text-white outline-none focus:border-blue-500/50" 
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] text-white/40 uppercase">Wallets</span>
                    </div>
                    <button 
                      onClick={generateSubWallets}
                      className="flex-[0.3] bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 text-[9px] font-bold uppercase transition-all"
                    >
                      Gen
                    </button>
                  </div>
                  
                  <div className="flex gap-2 mb-4">
                     <div className="relative flex-[0.5]">
                        <input 
                          type="text" 
                          value={mainFundAmount}
                          onChange={(e) => setMainFundAmount(e.target.value)}
                          className="w-full bg-black/50 border border-white/10 rounded-lg py-1.5 pl-2 pr-8 text-xs font-mono text-white outline-none focus:border-blue-500/50" 
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] text-white/40 uppercase">SOL</span>
                      </div>
                      <button 
                        onClick={handleFundAllFromMain}
                        disabled={isTrading || subWallets.length === 0}
                        className="flex-[0.5] py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/70 text-[9px] font-bold uppercase transition-all disabled:opacity-50"
                      >
                        {isTrading ? <Loader2 className="size-3 animate-spin mx-auto" /> : "Fund All"}
                      </button>
                  </div>

                  {/* Bundle Buy */}
                  <div className="flex bg-neutral-900 rounded-lg p-0.5 mb-2 mt-auto border border-white/5">
                    <button onClick={() => setAdvBuyMode("market")} className={`flex-1 text-[9px] py-1 rounded-md font-bold transition-all ${advBuyMode === "market" ? "bg-emerald-500/20 text-emerald-400" : "text-white/40 hover:text-white/80"}`}>Market</button>
                    <button onClick={() => setAdvBuyMode("limit")} className={`flex-1 text-[9px] py-1 rounded-md font-bold transition-all ${advBuyMode === "limit" ? "bg-emerald-500/20 text-emerald-400" : "text-white/40 hover:text-white/80"}`}>Limit</button>
                  </div>
                  
                  {advBuyMode === "limit" && (
                    <div className="flex gap-2 mb-2">
                      <select value={advBuyLimitType} onChange={(e) => setAdvBuyLimitType(e.target.value as any)} className="flex-[0.4] h-7 bg-neutral-950 border border-white/10 rounded px-2 text-[10px] text-white outline-none focus:border-emerald-500/50">
                        <option value="price">Price (SOL)</option>
                        <option value="marketcap">Marketcap</option>
                      </select>
                      <input type="number" step="any" placeholder="Target value..." value={advBuyLimitValue} onChange={(e) => setAdvBuyLimitValue(e.target.value)} className="flex-[0.6] h-7 bg-neutral-950 border border-white/10 rounded px-2 text-[10px] font-mono text-white placeholder:text-white/20 outline-none focus:border-emerald-500/50" />
                    </div>
                  )}
                  
                  <div className="relative mb-2">
                    <input 
                      type="text" 
                      value={bundleBuyAmount}
                      onChange={(e) => setBundleBuyAmount(e.target.value)}
                      className="w-full bg-black/50 border border-white/10 rounded-lg py-1.5 pl-3 pr-12 text-sm font-mono text-white outline-none focus:border-emerald-500/50" 
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-white/40 uppercase">SOL/W</span>
                  </div>
                  <button 
                    onClick={() => {
                      if (advBuyMode === "market") {
                        handleBundleBuy();
                      } else {
                        subWallets.forEach(w => handlePlaceLimitOrder("buy", advBuyLimitType, parseFloat(advBuyLimitValue), (w.allocSol || bundleBuyAmount).toString(), w.publicKey));
                      }
                    }}
                    disabled={isTrading || subWallets.length === 0}
                    className="w-full py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 rounded-lg text-emerald-400 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                  >
                    {isTrading ? <Loader2 className="size-3 animate-spin mx-auto" /> : (advBuyMode === "market" ? "Execute Buy All" : "Limit Buy (All)")}
                  </button>
                </div>

                {/* ADVANCED MULTI-WALLET: SELL & SUBWALLETS */}
                <div className="flex-[0.6] bg-black/40 border border-purple-500/20 rounded-xl p-3 flex flex-col max-h-64">
                  <div className="flex justify-between items-center mb-2">
                     <h3 className="text-[11px] font-black uppercase text-purple-400 flex items-center gap-2">
                       <Wallet className="size-3" /> Sub-Wallets ({subWallets.length})
                     </h3>
                     <button onClick={refreshSubWalletBalances} className="text-white/40 hover:text-white/80 transition-colors">
                        <Zap className="size-3" />
                     </button>
                  </div>
                  
                  {/* Wallets List */}
                  <div className="flex-1 overflow-y-auto mb-3 pr-1 space-y-1">
                    {subWallets.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center p-4 text-white/30">
                        <Users className="size-6 mb-1 opacity-50" />
                        <p className="text-[9px] font-medium">{t("panel.noSubWallets")}</p>
                      </div>
                    ) : (
                      subWallets.map((w, i) => (
                        <div key={w.id || i} className="flex flex-col bg-black/60 border border-white/5 p-1.5 rounded-lg hover:bg-white/5 group">
                           <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                 <span className="text-[10px] font-mono text-white/70">{w.publicKey.slice(0, 4)}...{w.publicKey.slice(-4)}</span>
                                 <button onClick={() => setFundingWallet(w)} className="opacity-0 group-hover:opacity-100 text-blue-400 transition-opacity p-1 hover:bg-blue-500/20 rounded">
                                   <QrCode className="size-3" />
                                 </button>
                              </div>
                              <div className="text-right flex flex-col justify-center">
                                 <div className="text-[9px] font-mono text-emerald-400">{w.tokenBalance.toLocaleString()} TKN</div>
                                 <div className="text-[9px] font-mono text-white/50">{w.solBalance.toFixed(3)} SOL</div>
                              </div>
                           </div>
                           <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-white/5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[8px] text-white/40 uppercase font-black">Buy</span>
                                <input 
                                  type="number" 
                                  placeholder={bundleBuyAmount}
                                  value={w.allocSol || ''}
                                  onChange={(e) => updateSubWallet(w.id, { allocSol: parseFloat(e.target.value) || undefined })}
                                  className="w-14 bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-white text-right focus:border-emerald-500/50 outline-none transition-colors"
                                />
                                <span className="text-[8px] text-white/30 font-mono">SOL</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[8px] text-white/40 uppercase font-black">Slip</span>
                                <input 
                                  type="number" 
                                  placeholder="15" 
                                  value={w.slippageBps || ''}
                                  onChange={(e) => updateSubWallet(w.id, { slippageBps: parseInt(e.target.value) || undefined })}
                                  className="w-14 bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[9px] font-mono text-white text-right focus:border-emerald-500/50 outline-none transition-colors"
                                />
                                <span className="text-[8px] text-white/30 font-mono">BPS</span>
                              </div>
                           </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="flex gap-2 mt-auto flex-col">
                    <div className="flex bg-neutral-900 rounded-lg p-0.5 border border-white/5">
                      <button onClick={() => setAdvSellMode("market")} className={`flex-1 text-[9px] py-1 rounded-md font-bold transition-all ${advSellMode === "market" ? "bg-rose-500/20 text-rose-400" : "text-white/40 hover:text-white/80"}`}>Market</button>
                      <button onClick={() => setAdvSellMode("limit")} className={`flex-1 text-[9px] py-1 rounded-md font-bold transition-all ${advSellMode === "limit" ? "bg-rose-500/20 text-rose-400" : "text-white/40 hover:text-white/80"}`}>Limit</button>
                    </div>
                    {advSellMode === "limit" && (
                      <div className="flex gap-2">
                        <select value={advSellLimitType} onChange={(e) => setAdvSellLimitType(e.target.value as any)} className="flex-[0.4] h-7 bg-neutral-950 border border-white/10 rounded px-2 text-[10px] text-white outline-none focus:border-rose-500/50">
                          <option value="price">Price (SOL)</option>
                          <option value="marketcap">Marketcap</option>
                        </select>
                        <input type="number" step="any" placeholder="Target value..." value={advSellLimitValue} onChange={(e) => setAdvSellLimitValue(e.target.value)} className="flex-[0.6] h-7 bg-neutral-950 border border-white/10 rounded px-2 text-[10px] font-mono text-white placeholder:text-white/20 outline-none focus:border-rose-500/50" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          if (advSellMode === "market") {
                            handleBundleSell();
                          } else {
                            subWallets.forEach(w => handlePlaceLimitOrder("sell", advSellLimitType, parseFloat(advSellLimitValue), "100%", w.publicKey));
                          }
                        }}
                        disabled={isTrading || subWallets.length === 0}
                        className="flex-1 py-1.5 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 rounded-lg text-rose-400 text-[9px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                      >
                        {isTrading ? <Loader2 className="size-3 animate-spin mx-auto" /> : (advSellMode === "market" ? "Sell 100%" : "Limit Sell 100%")}
                      </button>
                      <button 
                        onClick={handleConsolidate}
                        disabled={isTrading || subWallets.length === 0}
                        className="flex-1 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/50 hover:text-white/80 text-[9px] font-bold uppercase transition-all disabled:opacity-50"
                      >
                        Consolidate
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
      </div>
      
      {/* QR Code Funding Modal */}
      {fundingWallet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
             <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-black uppercase text-sm tracking-widest flex items-center gap-2">
                   <QrCode className="size-4 text-blue-400" /> Fund Sub-Wallet
                </h3>
                <button onClick={() => setFundingWallet(null)} className="text-white/40 hover:text-white transition-colors">
                   <X className="size-4" />
                </button>
             </div>
             
             <div className="bg-white rounded-xl p-4 flex justify-center mb-4 mx-auto w-fit">
                <QRCodeSVG value={fundingWallet.publicKey} size={200} />
             </div>
             
             <div className="bg-black/40 border border-white/5 rounded-xl p-3 flex items-center justify-between mb-4">
                <span className="font-mono text-sm text-white/80 break-all">{fundingWallet.publicKey}</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(fundingWallet.publicKey)}
                  className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/50 hover:text-white transition-colors flex-shrink-0 ml-2"
                >
                  <Copy className="size-4" />
                </button>
             </div>

             <div className="flex justify-between items-center text-xs">
                <span className="text-white/50 font-bold uppercase">Current Balance</span>
                <span className="font-mono text-emerald-400 font-bold">{fundingWallet.solBalance.toFixed(4)} SOL</span>
             </div>
             <button onClick={refreshSubWalletBalances} className="w-full mt-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
                Refresh Balances
             </button>
          </div>
        </div>
      )}
    </div>
  );
}
